import React from 'react'
import styled from 'styled-components'

import { CREDIT_REQUEST_POPUP_COPY, HOW_CREDITS_WORK_URL } from 'containers/Agents/creditConstants'

import Button from 'components/lib/DesignSystem/V3/Button'

import WalletCoinsIllustration from './WalletCoinsIllustration'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 440px;
  max-width: 100%;
  background: var(--color-elevated);
  border-radius: 16px;
  box-shadow:
    0px 20px 25px -5px rgba(0, 0, 0, 0.1),
    0px 8px 10px -6px rgba(0, 0, 0, 0.1);
  font-family: Poppins, sans-serif;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-bottom: 1px solid var(--card-border-color);
`

const CloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #223063;

  [data-theme='dark'] & {
    color: var(--color-text);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 28px 20px 40px;
  border-bottom: 1px solid var(--card-border-color);
`

// Figma 4584:149308 — the message block is centered in the card. Lines stay
// left-aligned inside it, so the shorter sentences sit under the title.
const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  text-align: left;
  max-width: 346px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: #223063;

  [data-theme='dark'] & {
    color: var(--color-text);
  }
`

const BodyLine = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #151b56;

  [data-theme='dark'] & {
    color: var(--sub-text-color);
  }
`

// Figma 4584:149296 — secondary reset-date line (90% opacity).
const ResetLine = styled(BodyLine)`
  opacity: 0.9;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
`

const HelpLink = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #151b56;
  cursor: pointer;

  [data-theme='dark'] & {
    color: var(--sub-text-color);
  }
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 2px;
  }
`

// Figma 4584:149299 — the "close-circle" layer renders as a plain bold X (no ring).
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const handleHowCreditsWork = (custom) => {
  if (typeof custom === 'function') return custom()
  globalThis?.open?.(HOW_CREDITS_WORK_URL, '_blank', 'noopener,noreferrer')
  return undefined
}

/** Figma 4584:149296 — PLG admin "Your team is out of AI credits" toast (PLG Experience §3.2, admin-only). */
const CreditsExhaustedPlgToast = ({
  requesterName,
  otherRequestersCount = 0,
  resetLabel,
  isAddCreditsLoading = false,
  isAddCreditsDisabled = false,
  addCreditsLabel = 'Add credits',
  onClose,
  onAddCredits,
  onHowCreditsWorks,
  id = 'credits-exhausted-plg-toast',
}) => {
  const copy = CREDIT_REQUEST_POPUP_COPY.PLG
  const subtext = copy.subtext({ userName: requesterName, otherCount: otherRequestersCount })

  return (
    <Card id={id} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
      <Header>
        <CloseButton id={`${id}-close`} type="button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <Body>
        <WalletCoinsIllustration scale={0.55} />
        <TextStack>
          <Title id={`${id}-title`}>{copy.title}</Title>
          <BodyLine id={`${id}-body`}>{subtext}</BodyLine>
          {resetLabel && (
            <ResetLine id={`${id}-reset`}>{`Free credits reset: ${resetLabel}`}</ResetLine>
          )}
        </TextStack>
      </Body>
      <Footer>
        <HelpLink
          id={`${id}-help`}
          type="button"
          onClick={() => handleHowCreditsWork(onHowCreditsWorks)}
        >
          How credits work?
        </HelpLink>
        <Button
          id={`${id}-add-credits`}
          size="medium"
          variant="primary"
          loading={isAddCreditsLoading}
          disabled={isAddCreditsDisabled}
          onClick={onAddCredits}
        >
          {addCreditsLabel}
        </Button>
      </Footer>
    </Card>
  )
}

export default CreditsExhaustedPlgToast
